import { createBrowserRouter, RouterProvider, Outlet, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { Suspense, lazy, useEffect } from 'react';
import { authRoutes } from '@/routes/auth';
import { profileRoutes } from '@/routes/profile';
import { staffRoutes } from '@/routes/staff';
import { tutorRoutes } from '@/routes/tutor';
import { clientRoutes } from '@/routes/client';
import { LoadingSpinner } from './components/ui/loading-spinner';
import { Alert, AlertDescription, AlertTitle } from './components/ui/alert';
import { AlertCircle, LogOut } from 'lucide-react';
import { Button } from './components/ui/button';
import { useQueryParam } from './hooks/useQueryParam';
import { captureException } from '@sentry/react';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      retry: 1,
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
    },
  },
});

queryClient.getQueryCache().subscribe((event) => {
  if (event.query.state.status === 'error') {
    console.error('🔴 Query error:', event.query.state.error);
    captureException(event.query.state.error);
  }
});

queryClient.getMutationCache().subscribe((event) => {
  if (event.mutation?.state.status === 'error') {
    console.error('🔴 Mutation error:', event.mutation.state.error);
    captureException(event.mutation.state.error);
  }
});

// Lazy load components
const ClientSidebar = lazy(() => import('@/components/ClientSidebar'));
const TutorSidebar = lazy(() => import('@/components/TutorSidebar'));
const StaffSidebar = lazy(() => import('@/components/StaffSidebar'));

const AppLayout = () => {
  const { userRoles, userRolesLoading } = useAuth();

  const renderSidebar = () => {
    if (userRolesLoading) return null;

    if (userRoles.includes('staff')) {
      return (
        <Suspense fallback={null}>
          <StaffSidebar />
        </Suspense>
      );
    }
    if (userRoles.includes('client')) {
      return (
        <Suspense fallback={null}>
          <ClientSidebar />
        </Suspense>
      );
    }
    if (userRoles.includes('tutor')) {
      return (
        <Suspense fallback={null}>
          <TutorSidebar />
        </Suspense>
      );
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      {renderSidebar()}
      <main
        className={`flex-1 ${userRoles.length > 0 ? 'ml-20' : ''} p-4 lg:p-8 max-w-[calc(100vw_-_5rem)]`}
      >
        <div className="max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

const FallbackRedirect = () => {
  const { user, userRoles, userRolesLoading, sessionLoading, signOut } = useAuth();
  const refreshed = useQueryParam('refreshed');

  console.log('Fallback', { userRolesLoading, userRoles, user, sessionLoading });

  // Wait a few seconds after logging in
  useEffect(() => {
    if (user && !userRoles && !refreshed) {
      const timeout = setTimeout(() => {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('refreshed', '1');
        window.location.href = currentUrl.toString();
      }, 3000);

      return () => clearTimeout(timeout);
    }
  }, [user, userRoles, refreshed]);

  if (userRolesLoading || sessionLoading || (user && !userRoles.length && !refreshed)) {
    return <LoadingSpinner />;
  }

  if (!userRoles.length && user && refreshed) {
    return (
      <div className="flex flex-col items-center justify-center h-[80vh] w-full">
        <div className="bg-white border p-6 rounded-lg flex flex-col gap-4 max-w-lg">
          <Alert variant="default" className="w-md">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Are you new around here?</AlertTitle>
            <AlertDescription>
              It can sometimes take a few minutes to sync your account if you're a new user. If
              that's the case try waiting a few seconds then refresh the page. If this doesn't work
              then make sure your account is set up in HubSpot. Otherwise please contact support and
              give them the following information:
            </AlertDescription>
          </Alert>

          <div>
            <p className="text-sm text-muted-foreground my-1">
              User ID: <span className="font-bold">{user.id}</span>
            </p>
            <p className="text-sm text-muted-foreground my-1">
              Email: <span className="font-bold">{user.email}</span>
            </p>
            {user.app_metadata?.provider ? (
              <p className="text-sm text-muted-foreground my-1">
                SSO Provider: <span className="font-bold">{user.app_metadata.provider}</span>
              </p>
            ) : null}
          </div>

          <Button
            variant="destructive"
            className="flex items-center justify-center gap-2 w-full sm:w-auto"
            onClick={async () => {
              await signOut.mutateAsync();
            }}
          >
            <LogOut className="h-4 w-4" />
            <span>Sign Out</span>
          </Button>
        </div>
      </div>
    );
  }

  if (userRoles.includes('staff')) {
    console.log('Redirecting to staff jobs');
    return <Navigate to="/staff/jobs" replace />;
  }
  if (userRoles.includes('client')) {
    console.log('Redirecting to client reports');
    return <Navigate to="/client/reports" replace />;
  }
  if (userRoles.includes('tutor')) {
    console.log('Redirecting to tutor jobs board');
    return <Navigate to="/tutor/jobs-board" replace />;
  }

  console.log('Redirecting to auth');
  return <Navigate to="/auth" replace />;
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <AppLayout />,
    children: [
      {
        index: true,
        element: <FallbackRedirect />,
      },
      {
        path: '*',
        element: <FallbackRedirect />,
      },
      ...authRoutes,
      ...profileRoutes,
      ...staffRoutes,
      ...tutorRoutes,
      ...clientRoutes,
    ],
  },
]);

export const Router = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};
